import type { Area } from 'react-easy-crop';
import type { ProfilePictureCropRect } from '@/types/person';

export const PROFILE_PICTURE_SIZE = 512;

export function areaToCropRect(area: Area): ProfilePictureCropRect {
    return {
        x: Math.round(area.x),
        y: Math.round(area.y),
        w: Math.round(area.width),
        h: Math.round(area.height),
    };
}

function loadImage(
    source: string,
    signal?: AbortSignal,
): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        const cleanup = () => {
            image.onload = null;
            image.onerror = null;
        };
        const onAbort = () => {
            cleanup();
            image.src = '';
            reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
        };
        if (signal?.aborted) {
            onAbort();
            return;
        }
        signal?.addEventListener('abort', onAbort, { once: true });
        image.onload = () => {
            signal?.removeEventListener('abort', onAbort);
            cleanup();
            resolve(image);
        };
        image.onerror = () => {
            signal?.removeEventListener('abort', onAbort);
            cleanup();
            reject(new Error('Failed to load image'));
        };
        image.src = source;
    });
}

export async function cropImageToBlob(
    imageSource: string,
    crop: ProfilePictureCropRect,
    size = PROFILE_PICTURE_SIZE,
    signal?: AbortSignal,
): Promise<Blob> {
    const image = await loadImage(imageSource, signal);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Canvas 2D context unavailable');
    }
    context.drawImage(image, crop.x, crop.y, crop.w, crop.h, 0, 0, size, size);
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
            return;
        }
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to encode image'));
                }
            },
            'image/jpeg',
            0.9,
        );
    });
}
